import React, { useEffect, useRef, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { useInView } from "react-hook-inview"
import { easeOut, longAnim } from "utils/gsapAnims"
import { renderClasses } from "utils/renderClasses"
import { separateChildrenFromLine } from "utils/separateChildTagsFromLine"
import Button from "components/elements/styledButton"
import Title from "components/elements/Title"
import Heading5 from "components/type/Heading5"
import Wysiwyg from "components/type/Wysiwyg"
import "./textLockup.scss"

const TextLockup = ({
  action,
  alignment = "left",
  eyebrow,
  secondaryAction,
  shouldAnimateIn = true,
  text,
  title,
}) => {
  const [ref, inView] = useInView({ threshold: 0.65, unobserveOnEnter: true })
  const eyebrowRef = useRef()
  const titleRef = useRef()
  const textRef = useRef()
  const [split, setSplit] = useState()

  const initAnimation = useCallback(() => {
    if (
      !shouldAnimateIn ||
      typeof window === "undefined" ||
      !window.gsap ||
      !window.SplitText
    )
      return

    const gsap = window.gsap
    const SplitText = window.SplitText

    if (eyebrowRef.current) {
      gsap.set(eyebrowRef.current, { opacity: 0, y: "1em" })
    }

    if (titleRef.current) {
      const q = gsap.utils.selector(titleRef.current)
      const s = new SplitText(q(".g-heading-tag"), {
        linesClass: "line",
        type: "lines, words",
      })

      s.lines.forEach(line => {
        const [divs, otherTags] = separateChildrenFromLine(line)
        gsap.set([...divs, ...otherTags], { opacity: 0, y: "1em" })
      })

      setSplit(s)
    }

    if (textRef.current) {
      gsap.set(textRef.current, { opacity: 0, y: "1em" })
    }

    gsap.set(ref.current, { opacity: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAnimateIn])

  useEffect(() => {
    initAnimation()
  }, [initAnimation])

  useEffect(() => {
    if (
      !shouldAnimateIn ||
      !inView ||
      !split ||
      typeof window === "undefined" ||
      !window.gsap
    )
      return

    const gsap = window.gsap
    const delay = 0.033
    const duration = longAnim
    let delayCount = 0

    if (eyebrow) {
      gsap.to(eyebrowRef.current, {
        delay: delay * delayCount,
        duration,
        ease: easeOut,
        opacity: 1,
        y: 0,
      })
      delayCount += 1
    }

    split.lines.forEach((line, index) => {
      const [divs, otherTags] = separateChildrenFromLine(line)
      gsap.to([...divs, ...otherTags], {
        delay: delay * (delayCount + index),
        duration,
        ease: easeOut,
        opacity: 1,
        y: 0,
      })
    })

    delayCount += split.lines.length

    if (text && textRef.current) {
      gsap.to(textRef.current, {
        delay: delay * delayCount,
        duration,
        ease: easeOut,
        opacity: 1,
        y: 0,
      })
    }
  }, [inView, shouldAnimateIn, split, text, eyebrow])

  useEffect(() => {
    if (
      shouldAnimateIn &&
      inView &&
      text &&
      textRef.current &&
      textRef.current.innerText.trim().length > 0 &&
      getComputedStyle(textRef.current).opacity === "0" &&
      typeof window !== "undefined" &&
      window.gsap
    ) {
      window.gsap.to(textRef.current, {
        opacity: 1,
        y: 0,
        duration: longAnim,
        ease: easeOut,
      })
    }
  }, [shouldAnimateIn, inView, text])
  const handleAnchorScroll = (e, href = "") => {
    if (href.includes("#")) {
      e.preventDefault()
      const [, hash] = href.split("#")
      const el = document.getElementById(hash)
      if (el) {
        el.scrollIntoView({ behavior: "smooth" })
      }
    }
  }

  return (
    <div
      className={renderClasses(`text-lockup --${alignment}`, [
        [shouldAnimateIn, "tl-prep"],
      ])}
      ref={ref}
      style={{ opacity: 1 }}
    >
      {eyebrow && <Heading5 ref={eyebrowRef}>{eyebrow}</Heading5>}
      {title && <Title {...title} ref={titleRef} />}
      {text && <Wysiwyg ref={textRef} text={{ ...text }} />}
      {(action || secondaryAction) && (
        <div className="text-lockup__button-group">
          {action && (
            <Button
              {...action}
              className="text-lockup__button"
              onClick={e => handleAnchorScroll(e, action.href)}
            />
          )}
          {secondaryAction && (
            <Button
              {...secondaryAction}
              className="text-lockup__button"
              onClick={e => handleAnchorScroll(e, secondaryAction.href)}
            />
          )}
        </div>
      )}
    </div>
  )
}

TextLockup.props = {
  action: PropTypes.shape(Button.props),
  alignment: PropTypes.oneOf(["left", "center", "right"]),
  eyebrow: PropTypes.string,
  shouldAnimateIn: PropTypes.bool,
  text: PropTypes.shape(Wysiwyg.props),
  title: PropTypes.shape(Title.props),
}

TextLockup.propTypes = TextLockup.props

export default TextLockup
